import type {
  DistributionStatus,
  FinanceFacts,
  FounderDistributions,
  ReserveLine,
} from '@mfos/shared';
import { Link } from 'react-router';
import { Badge } from '../../components/Badge';
import { formatDate } from '../../lib/format';
import { phrase, useI18n } from '../../lib/i18n/runtime';
import { en } from '../../lib/i18n/messages';
import {
  DISTRIBUTION_STATUS,
  distributionsBase,
  distributionRef,
} from '../distributions/distributions-display';
import { formatMonth } from '../finance/finance-display';
import { MoneyText } from '../finance/finance-format';
import { valuationRef } from '../valuation/valuation-display';
import { ConfidenceBadge, RangeFigures, ValuationDisclaimer } from '../valuation/valuation-format';

/** Month, cash, reserve, funding due and the latest valuation. Combined totals appear only when complete. */
export function FinanceFactsView({ facts, base }: { facts: FinanceFacts; base: string }) {
  return (
    <>
      <MonthSection facts={facts} base={base} />
      <CashSection facts={facts} base={base} />
      <ReserveSection lines={facts.reserve} base={base} />
      <FundingDueSection facts={facts} base={base} />
      <ValuationSection facts={facts} base={base} />
    </>
  );
}

function shownNote(note: string | null, t: ReturnType<typeof useI18n>['t']): string | null {
  if (note === null) return null;
  if (note === en.noReportingCurrency) return t('noReportingCurrency');
  return note;
}

function MonthSection({ facts, base }: { facts: FinanceFacts; base: string }) {
  const month = facts.month;
  const { t } = useI18n();
  const note = shownNote(month.note, t);
  return (
    <section className="card stack" aria-labelledby="month-facts-heading">
      <div className="section-header">
        <h3 id="month-facts-heading">
          {t('thisMonth')} — {formatMonth(month.month)}
        </h3>
        <Link to={`${base}/finance/pnl`}>{t('openPnl')}</Link>
      </div>
      <p className="meta">
        {t('journalAsOf')} <time dateTime={facts.asOf}>{formatDate(facts.asOf)}</time>.{' '}
        {t('resultIs')}
      </p>
      {note !== null && <p className="notice notice--info">{note}</p>}
      {month.byCurrency.length === 0 ? (
        <p className="meta">{t('noIncome')}</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <caption className="visually-hidden">
              {phrase('Income and expenses by currency')}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t('currency')}</th>
                <th scope="col">{t('income')}</th>
                <th scope="col">{t('expenses')}</th>
                <th scope="col">{t('result')}</th>
              </tr>
            </thead>
            <tbody>
              {month.byCurrency.map((row) => (
                <tr key={row.currency}>
                  <th scope="row">{row.currency}</th>
                  <td>
                    <MoneyText money={{ amount: row.revenue, currency: row.currency }} />
                  </td>
                  <td>
                    <MoneyText money={{ amount: row.expenses, currency: row.currency }} />
                  </td>
                  <td>
                    <MoneyText money={{ amount: row.result, currency: row.currency }} />
                  </td>
                </tr>
              ))}
              {month.combined !== null && (
                <tr>
                  <th scope="row">Combined ({month.combined.currency})</th>
                  <td>
                    <MoneyText
                      money={{ amount: month.combined.revenue, currency: month.combined.currency }}
                    />
                  </td>
                  <td>
                    <MoneyText
                      money={{
                        amount: month.combined.expenses,
                        currency: month.combined.currency,
                      }}
                    />
                  </td>
                  <td>
                    <MoneyText
                      money={{ amount: month.combined.result, currency: month.combined.currency }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CashSection({ facts, base }: { facts: FinanceFacts; base: string }) {
  const cash = facts.cash;
  const { t } = useI18n();
  const note = shownNote(cash.note, t);
  return (
    <section className="card stack" aria-labelledby="cash-facts-heading">
      <div className="section-header">
        <h3 id="cash-facts-heading">{t('cash')}</h3>
        <Link to={`${base}/finance/cash`}>{t('openCash')}</Link>
      </div>
      <p className="meta">{t('cashMeta')}</p>
      {note !== null && <p className="notice notice--info">{note}</p>}
      {cash.totals.length === 0 ? (
        <p className="meta">{t('noCash')}</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <caption className="visually-hidden">{phrase('Cash by currency')}</caption>
            <thead>
              <tr>
                <th scope="col">{phrase('Currency')}</th>
                <th scope="col">{phrase('Book balance')}</th>
                <th scope="col">{phrase('Operating')}</th>
                <th scope="col">{phrase('Bank-reported')}</th>
              </tr>
            </thead>
            <tbody>
              {cash.totals.map((total) => (
                <tr key={total.currency}>
                  <th scope="row">{total.currency}</th>
                  <td>
                    <MoneyText money={{ amount: total.book, currency: total.currency }} />
                  </td>
                  <td>
                    <MoneyText money={{ amount: total.operating, currency: total.currency }} />
                  </td>
                  <td>
                    {total.observed === null ? (
                      'No bank-reported balance'
                    ) : (
                      <MoneyText money={{ amount: total.observed, currency: total.currency }} />
                    )}
                  </td>
                </tr>
              ))}
              {cash.combined !== null && (
                <tr>
                  <th scope="row">Combined ({cash.combined.currency})</th>
                  <td>
                    <MoneyText
                      money={{ amount: cash.combined.book, currency: cash.combined.currency }}
                    />
                  </td>
                  <td>
                    <MoneyText
                      money={{
                        amount: cash.combined.operating,
                        currency: cash.combined.currency,
                      }}
                    />
                  </td>
                  <td>{phrase('Bank reports stay in each currency')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ReserveSection({ lines, base }: { lines: readonly ReserveLine[]; base: string }) {
  const { t } = useI18n();
  return (
    <section className="card stack" aria-labelledby="reserve-facts-heading">
      <div className="section-header">
        <h3 id="reserve-facts-heading">{t('reserve')}</h3>
        <Link to={`${base}/finance/distributions/reserve`}>{t('openReserve')}</Link>
      </div>
      <p className="meta">{t('reserveMeta')}</p>
      {lines.length === 0 ? (
        <p className="meta">{t('noReserve')}</p>
      ) : (
        <ul className="item-list">
          {lines.map((line) => (
            <li key={line.currency} className="item">
              <h4 className="item__title">{line.currency}</h4>
              <p className="meta">
                Calculated on{' '}
                <time dateTime={line.calculatedOn}>{formatDate(line.calculatedOn)}</time>
                {line.policySummary !== null ? ` · ${line.policySummary}` : ' · No reserve policy'}
              </p>
              <dl className="stat-grid" aria-label={`${line.currency} reserve`}>
                <div>
                  <dt>{phrase('Operating cash')}</dt>
                  <dd>
                    <MoneyText money={{ amount: line.operatingCash, currency: line.currency }} />
                  </dd>
                </div>
                <div>
                  <dt>{phrase('Reserve required')}</dt>
                  <dd>
                    {line.reserveRequired === null ? (
                      'Not computed'
                    ) : (
                      <MoneyText
                        money={{ amount: line.reserveRequired, currency: line.currency }}
                      />
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{phrase('Distributable')}</dt>
                  <dd>
                    {line.distributable === null ? (
                      'Not computed'
                    ) : (
                      <MoneyText money={{ amount: line.distributable, currency: line.currency }} />
                    )}
                  </dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FundingDueSection({ facts, base }: { facts: FinanceFacts; base: string }) {
  const funding = facts.fundingDue;
  const { t } = useI18n();
  return (
    <section className="card stack" aria-labelledby="funding-due-heading">
      <div className="section-header">
        <h3 id="funding-due-heading">{t('fundingDue')}</h3>
        <Link to={`${base}/finance/funding`}>{t('openFunding')}</Link>
      </div>
      <p className="meta">
        {t('due')} {formatDate(funding.period.from)} – {formatDate(funding.period.to)}.
      </p>
      {funding.byCurrency.length === 0 ? (
        <p className="meta">{t('noFunding')}</p>
      ) : (
        <ul className="plain-list">
          {funding.byCurrency.map((row) => (
            <li key={row.currency}>
              <MoneyText money={{ amount: row.outstanding, currency: row.currency }} /> outstanding
              {/[1-9]/.test(row.overdue) && (
                <>
                  {' '}
                  · <MoneyText money={{ amount: row.overdue, currency: row.currency }} />{' '}
                  {phrase('overdue')}
                </>
              )}
              {' · '}
              {row.calls} call{row.calls === 1 ? '' : 's'}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ValuationSection({ facts, base }: { facts: FinanceFacts; base: string }) {
  const latest = facts.valuation.latest;
  const { t } = useI18n();
  const root = `${base}/finance/valuation`;
  return (
    <section className="card stack" aria-labelledby="valuation-facts-heading">
      <div className="section-header">
        <h3 id="valuation-facts-heading">{t('latestValuation')}</h3>
        <Link to={root}>{t('openValuations')}</Link>
      </div>
      <ValuationDisclaimer text={facts.valuation.disclaimer} />
      {latest === null ? (
        <p className="meta">{t('noValuation')}</p>
      ) : (
        <>
          <p>
            <Link to={`${root}/${latest.id}`}>{valuationRef(latest.number)}</Link> as of{' '}
            {formatDate(latest.valuationDate)} in {latest.currency}{' '}
            <ConfidenceBadge confidence={latest.confidence} />
          </p>
          <p className="meta">
            {phrase('The range is the result. Confidence describes how complete the inputs are.')}
          </p>
          <RangeFigures
            range={latest.equityValue}
            currency={latest.currency}
            label={`Indicative equity value of ${valuationRef(latest.number)}`}
          />
        </>
      )}
    </section>
  );
}

export function MyDistributions({
  distributions,
  workspaceId,
}: {
  distributions: FounderDistributions;
  workspaceId: string;
}) {
  const base = `/w/${workspaceId}`;
  return (
    <section className="card stack" aria-labelledby="my-distributions-heading">
      <div className="section-header">
        <h3 id="my-distributions-heading">{phrase('My distributions')}</h3>
        <Link to={`${base}/finance/distributions`}>{phrase('Open distributions')}</Link>
      </div>
      {distributions.items.length === 0 ? (
        <p className="meta">{phrase('No distribution has been declared for you.')}</p>
      ) : (
        <ul className="item-list">
          {distributions.items.map((item) => (
            <li key={item.allocationId} className="item">
              <div className="item__header">
                <h4 className="item__title">
                  <Link to={`${distributionsBase(workspaceId)}/${item.distribution.id}`}>
                    {distributionRef(item.distribution.number)}
                    {item.distribution.title !== null ? ` · ${item.distribution.title}` : ''}
                  </Link>
                </h4>
                <DistributionState status={item.distribution.status} />
              </div>
              <p className="meta">Record date {formatDate(item.distribution.recordDate)}</p>
              <p>
                {phrase('Declared')} <MoneyText money={item.gross} /> · paid{' '}
                <MoneyText money={item.paid} /> · outstanding <MoneyText money={item.outstanding} />
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DistributionState({ status }: { status: DistributionStatus }) {
  const display = DISTRIBUTION_STATUS[status];
  return <Badge tone={display.tone} label={display.label} />;
}
